import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const AdminUserTable = ({ users, onUpdate, onDelete, onSelectUser, loading }) => {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const handleEdit = (user) => {
    setEditingId(user?.id);
    setEditForm({
      full_name: user?.full_name,
      email: user?.email,
      role: user?.role,
      is_active: user?.is_active
    });
  };

  const handleSave = async (userId) => {
    const result = await onUpdate(userId, editForm);
    if (result?.success) {
      setEditingId(null);
      setEditForm({});
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'agent':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'operator':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 shadow-soft">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg shadow-soft overflow-hidden">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-foreground">User Accounts</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Manage administrator and operator accounts
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                User
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {users?.map((user) => (
              <tr
                key={user?.id}
                className="hover:bg-muted/20 transition-colors"
              >
                {editingId === user?.id ? (
                  // Edit Mode
                  (<>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <input
                          type="text"
                          value={editForm?.full_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, full_name: e?.target?.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Full Name"
                        />
                        <input
                          type="email"
                          value={editForm?.email || ''}
                          onChange={(e) => setEditForm({ ...editForm, email: e?.target?.value })}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          placeholder="Email"
                          disabled
                        />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={editForm?.role || ''}
                        onChange={(e) => setEditForm({ ...editForm, role: e?.target?.value })}
                        className="px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      >
                        <option value="admin">Admin</option>
                        <option value="agent">Agent</option>
                        <option value="operator">Operator</option>
                      </select>
                    </td>
                    <td className="px-6 py-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={editForm?.is_active || false}
                          onChange={(e) => setEditForm({ ...editForm, is_active: e?.target?.checked })}
                          className="rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-foreground">Active</span>
                      </label>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {formatDate(user?.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleSave(user?.id)}
                          className="p-2 text-accent hover:bg-accent/10 rounded-lg transition-colors"
                          title="Save"
                        >
                          <Icon name="Check" size={16} />
                        </button>
                        <button
                          onClick={handleCancel}
                          className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          title="Cancel"
                        >
                          <Icon name="X" size={16} />
                        </button>
                      </div>
                    </td>
                  </>)
                ) : (
                  // View Mode
                  (<>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <Icon name="User" size={20} color="var(--color-primary)" />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {user?.full_name || 'Unknown User'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {user?.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user?.role)}`}>
                        {user?.role || 'N/A'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${user?.is_active ? 'bg-accent' : 'bg-error'}`}></div>
                        <span className="text-sm text-foreground">
                          {user?.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {formatDate(user?.created_at)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(user)}
                          className="p-2 text-primary hover:bg-primary/10 rounded-lg transition-colors"
                          title="Edit User"
                        >
                          <Icon name="Edit" size={16} />
                        </button>
                        <button
                          onClick={() => onDelete(user?.id)}
                          className="p-2 text-error hover:bg-error/10 rounded-lg transition-colors"
                          title="Deactivate User"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      </div>
                    </td>
                  </>)
                )}
              </tr>
            ))}
          </tbody>
        </table>

        {users?.length === 0 && (
          <div className="p-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Users" size={32} color="var(--color-muted-foreground)" />
            </div>
            <p className="text-muted-foreground">No users found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUserTable;